"use client"

import { useState, useEffect, useMemo } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import DesignAdventureApp from "./App"
import { useLanguage } from "./LanguageContext"
import { getAvailableGames, loadAppConfiguration } from "./ContentLoader"
import Navigation, { Footer } from "./Navigation"
import "./components/GameSelector.css"
import AboutPage from "./AboutPage"

const GameSelector = () => {
  const [selectedGame, setSelectedGame] = useState(() => {
    const hash = window.location.hash.replace("#", "")
    const saved = localStorage.getItem("selectedGame")
    return hash || saved || null
  })

  // Add a new state for showing the About page
  const [showAboutPage, setShowAboutPage] = useState(false)
  const [aboutContent, setAboutContent] = useState(null)

  // Get current language from context
  const { language } = useLanguage()

  // State for app configuration - starts with null and will be populated from JSON
  const [appConfig, setAppConfig] = useState(null)

  // Helper function to get localized text from appConfig
  const t = (path, defaultText = "") => {
    // If appConfig isn't loaded yet, return the default text
    if (!appConfig) return defaultText

    // Split the path by dots to navigate nested objects
    const keys = path.split(".")
    let value = appConfig

    // Navigate through the object
    for (const key of keys) {
      if (!value || !value[key]) return defaultText
      value = value[key]
    }

    // Return the localized text if it's an object with language keys
    if (value && typeof value === "object" && value[language.code]) {
      return value[language.code]
    }

    // Return the value if it's a string or the default text
    return typeof value === "string" ? value : value.en || defaultText
  }

  // Game listing state
  const [games, setGames] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isBetaMode, setIsBetaMode] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const gamesPerPage = 24

  // Add a function to load the About page content
  useEffect(() => {
    if (showAboutPage && !aboutContent) {
      const fetchAboutContent = async () => {
        try {
          const response = await fetch(`/content/main/app-about.json`)
          if (!response.ok) {
            throw new Error(`Failed to load about content: ${response.status}`)
          }
          const data = await response.json()
          setAboutContent(data)
        } catch (error) {
          console.error("Failed to load about content:", error)
        }
      }
      fetchAboutContent()
    }
  }, [showAboutPage, aboutContent])

  // Handle navigation
  const handleNavigation = (page) => {
    setShowAboutPage(page === "about")
  }

  // Load app configuration
  useEffect(() => {
    const fetchAppConfig = async () => {
      try {
        // You can customize the company ID here to load company-specific branding
        // For example: const companyId = 'microsoft'; or get it from URL params
        const companyId = new URLSearchParams(window.location.search).get("company") || null

        // Load the configuration with optional company ID
        const config = await loadAppConfiguration(language.code, companyId, isBetaMode)
        setAppConfig(config)
        console.log("Loaded app configuration:", config)

        // Apply accent color if provided
        if (config?.branding?.accentColor) {
          document.documentElement.style.setProperty("--accent-color", config.branding.accentColor)
        }
      } catch (error) {
        console.error("Failed to load app configuration:", error)
      }
    }

    fetchAppConfig()
  }, [language.code, isBetaMode])

  // Check for beta mode in URL
  useEffect(() => {
    const checkBetaMode = () => {
      const isBeta = window.location.pathname.includes("/beta")
      setIsBetaMode(isBeta)
    }

    checkBetaMode()
    window.addEventListener("popstate", checkBetaMode)
    window.addEventListener("hashchange", checkBetaMode)

    return () => {
      window.removeEventListener("hashchange", checkBetaMode)
      window.removeEventListener("popstate", checkBetaMode)
    }
  }, [])

  // Load games
  useEffect(() => {
    const loadGames = async () => {
      setIsLoading(true)
      try {
        const availableGames = await getAvailableGames(language.code, isBetaMode)
        setGames(
          availableGames.map((game) => ({
            ...game,
            isBeta: isBetaMode,
          })),
        )
      } catch (error) {
        console.error("Error loading games:", error)
        setGames([])
      } finally {
        setIsLoading(false)
      }
    }

    loadGames()
  }, [language.code, isBetaMode])

  // Handle localStorage and URL hash
  useEffect(() => {
    if (selectedGame) {
      localStorage.setItem("selectedGame", selectedGame)
      window.location.hash = selectedGame
    } else {
      localStorage.removeItem("selectedGame")
      window.location.hash = ""
    }
  }, [selectedGame])

  // Handle URL hash changes
  useEffect(() => {
    const hash = window.location.hash.replace("#", "")
    const saved = localStorage.getItem("selectedGame")

    if (hash && !games.some((game) => game.id === hash)) {
      window.history.replaceState(null, "", window.location.pathname)
      setSelectedGame(null)
    } else {
      setSelectedGame(hash || saved || null)
    }
  }, [games])

  // Sort games by featured status and title
  const sortedGames = useMemo(() => {
    return [...games].sort((a, b) => {
      if (a.featured && !b.featured) return -1
      if (!a.featured && b.featured) return 1
      return a.title.localeCompare(b.title)
    })
  }, [games])

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(sortedGames.length / gamesPerPage))
  const paginatedGames = useMemo(() => {
    const startIndex = (currentPage - 1) * gamesPerPage
    return sortedGames.slice(startIndex, startIndex + gamesPerPage)
  }, [sortedGames, currentPage, gamesPerPage])

  // Handle page changes
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)))
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  if (showAboutPage) {
    return <AboutPage onReturnHome={() => setShowAboutPage(false)} />
  }

  // If a game is selected, render the main app with the selected game
  if (selectedGame) {
    return <DesignAdventureApp gameId={selectedGame} onReturnHome={() => setSelectedGame(null)} beta={isBetaMode} />
  }

  return (
    <div className="game-selector-container">
      <Navigation appConfig={appConfig} currentPage="home" onNavigate={handleNavigation} t={t} />

      <main className="main-content">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content">
            <div className="hero-content-inner">
              <h1 className="hero-title">{t("heroSection.title", "Learning Adventures")}</h1>
              <p className="hero-subtitle">
                {t("heroSection.subtitle", "Games that build digital fluency through play")}
              </p>
            </div>
          </div>
        </section>

        {/* Games Section */}
        <section className="games-section">
          <div className="games-content">
            {/* Game Grid */}
            {isLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
              </div>
            ) : sortedGames.length === 0 ? (
              <div className="no-games-container">
                <div className="no-games-emoji">🔍</div>
                <h2 className="no-games-title">{t("interface.noLessonsFound", "No adventures found")}</h2>
                <p className="no-games-text">{t("interface.tryAdjusting", "Try adjusting your search or filters")}</p>
              </div>
            ) : (
              <div className="games-grid">
                {paginatedGames.map((game) => (
                  <button
                    key={game.id}
                    onClick={() => setSelectedGame(game.id)}
                    className={`game-card ${game.bgColor} ${game.featured ? "featured" : ""}`}
                  >
                    {/* Featured badge */}
                    {game.featured && (
                      <div className="featured-badge">
                        {game.featuredText || (language.code === "nl" ? "Uitgelicht" : "Featured")}
                      </div>
                    )}

                    <div className="game-card-header">
                      <div className="game-emoji">{game.emoji}</div>
                      <div className="game-info">
                        <h2 className="game-title">{game.title}</h2>

                        {/* Difficulty & Age Range Pills */}
                        <div className="game-meta">
                          <span className="game-meta-pill">{game.difficulty}</span>

                          {/* Language indicator */}
                          {game.language && game.language !== language.code && (
                            <span className="game-meta-pill language">
                              {game.language === "en" ? "🇬🇧" : game.language === "nl" ? "🇳🇱" : game.language}
                            </span>
                          )}
                        </div>

                        {/* Description */}
                        <p className="game-description">{game.description}</p>
                      </div>
                    </div>

                    {/* Tags at bottom */}
                    {game.tags && game.tags.length > 0 && (
                      <div className="game-tags">
                        <div className="game-tags-container">
                          {game.tags.slice(0, 3).map((tag, index) => (
                            <span key={index} className="game-tag">
                              #{tag}
                            </span>
                          ))}
                          {game.tags.length > 3 && <span className="game-tags-more">+{game.tags.length - 3} more</span>}
                        </div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <nav className="pagination-controls" aria-label="Pagination">
                  {/* Previous Page */}
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`pagination-button left ${currentPage === 1 ? "disabled" : "active"}`}
                  >
                    <span className="sr-only">Back</span>
                    <ChevronLeft className="pagination-icon" />
                  </button>

                  {/* Mobile page indicator */}
                  <span className="pagination-indicator">
                    {currentPage} / {totalPages}
                  </span>

                  {/* Next Page */}
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`pagination-button right ${currentPage === totalPages ? "disabled" : "active"}`}
                  >
                    <span className="sr-only">Continue</span>
                    <ChevronRight className="pagination-icon" />
                  </button>
                </nav>
              </div>
            )}
          </div>
        </section>

        <div className="coming-soon-section">
          <h3 className="coming-soon-title">{t("comingSoon.title", "✨ More Adventures Coming Soon! ✨")}</h3>
          <p className="coming-soon-text">
            {t(
              "comingSoon.text",
              "We're crafting exciting new lessons to help you unlock even more digital skills. Stay tuned for updates—you won't want to miss what's next!",
            )}
          </p>
        </div>
      </main>

      <Footer appConfig={appConfig} t={t} />
    </div>
  )
}

export default GameSelector
